import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { ShieldCheck, ArrowLeft, Users, X, ChevronLeft, ChevronRight, Share2 } from "lucide-react";

const SOCIAL_LABELS = { instagram: "Instagram", youtube: "YouTube", x: "X", tiktok: "TikTok", facebook: "Facebook", website: "Website" };
const SOCIAL_KEYS = ["instagram", "youtube", "x", "tiktok", "facebook", "website"];
const AMBER = "#C2571F";
const LIME = "#cfe86b";

function SocialIcon({ k }) {
  const cls = "w-3.5 h-3.5 shrink-0";
  const stroke = { className: cls, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (k) {
    case "instagram":
      return (<svg {...stroke}><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4.2" /><circle cx="17.6" cy="6.4" r="1.1" fill="currentColor" stroke="none" /></svg>);
    case "facebook":
      return (<svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 21v-7h2.4l.4-2.9h-2.8V9.2c0-.85.24-1.43 1.46-1.43H16.4V5.16A20 20 0 0 0 13.87 5c-2.1 0-3.55 1.29-3.55 3.66v2.04H8v2.9h2.32V21z" /></svg>);
    case "youtube":
      return (<svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M23 12s0-3.2-.4-4.7a2.5 2.5 0 0 0-1.76-1.77C19.28 5.13 12 5.13 12 5.13s-7.28 0-8.84.4A2.5 2.5 0 0 0 1.4 7.3C1 8.8 1 12 1 12s0 3.2.4 4.7a2.5 2.5 0 0 0 1.76 1.77c1.56.4 8.84.4 8.84.4s7.28 0 8.84-.4a2.5 2.5 0 0 0 1.76-1.77C23 15.2 23 12 23 12zM9.75 15.02V8.98L15.5 12z" /></svg>);
    case "x":
      return (<svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.22-6.82-5.97 6.82H1.66l7.73-8.84L1.25 2.25h6.83l4.71 6.23zm-1.16 17.52h1.83L7.01 4.13H5.05z" /></svg>);
    case "tiktok":
      return (<svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M16.6 5.82a4.28 4.28 0 0 1-1.06-2.82h-3.02v12.06a2.44 2.44 0 1 1-2.44-2.44c.2 0 .4.03.59.08V9.62a5.52 5.52 0 0 0-.59-.03A5.47 5.47 0 1 0 15.55 15V9.01a7.28 7.28 0 0 0 4.25 1.36V7.35a4.28 4.28 0 0 1-3.2-1.53z" /></svg>);
    default:
      return (<svg {...stroke}><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18" /></svg>);
  }
}

function socialDisplay(k, v) {
  let s = (v || "").trim();
  if (k === "website") return s.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/, "");
  if (/^https?:\/\//i.test(s)) {
    const parts = s.replace(/\/+$/, "").split("/").filter(Boolean);
    s = parts[parts.length - 1] || s;
  }
  return s.replace(/^@/, "");
}

function splitBio(bio) {
  const raw = bio || "";
  const lines = raw.split("\n");
  let lastIdx = -1;
  for (let i = lines.length - 1; i >= 0; i--) { if (lines[i].trim()) { lastIdx = i; break; } }
  if (lastIdx >= 0 && /^#\S+$/.test(lines[lastIdx].trim())) {
    return { body: lines.slice(0, lastIdx).join("\n").trimEnd(), signature: lines[lastIdx].trim() };
  }
  return { body: raw, signature: null };
}

function buildSocialLinks(socials) {
  const out = [];
  for (const k of SOCIAL_KEYS) {
    const v = (socials || {})[k];
    if (k === "instagram") {
      (Array.isArray(v) ? v : (v ? [v] : [])).forEach((h, i) => { if (h) out.push({ k, v: h, key: `instagram-${i}` }); });
    } else if (v) {
      out.push({ k, v, key: k });
    }
  }
  return out;
}

export default function AmbassadorDetail() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [amb, setAmb] = useState(undefined); // undefined = loading, null = not found
  const [lightbox, setLightbox] = useState(null); // active photo index, or null

  useEffect(() => {
    let alive = true;
    setAmb(undefined);
    api.get(`/ambassadors/${slug}`)
      .then(({ data }) => { if (alive) setAmb(data); })
      .catch(() => { if (alive) setAmb(null); });
    return () => { alive = false; };
  }, [slug]);

  const photos = amb?.photos || [];

  // Deep link: /ambassadors/:slug?p=2 opens that exact photo full-size on arrival.
  useEffect(() => {
    if (!amb || !photos.length) return;
    const p = parseInt(sp.get("p"), 10);
    if (!Number.isNaN(p) && p >= 0 && p < photos.length) setLightbox(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amb]);

  const close = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox((i) => (i === null ? i : (i - 1 + photos.length) % photos.length)), [photos.length]);
  const next = useCallback(() => setLightbox((i) => (i === null ? i : (i + 1) % photos.length)), [photos.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, prev, next]);

  return (
    <div className="min-h-screen text-foreground" data-testid="ambassador-detail-page">
      <Navbar />
      <div className="max-w-[960px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="flex items-center justify-between gap-4">
          <Link to="/ambassadors" data-testid="ambassador-detail-back"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[2px] text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> All ambassadors
          </Link>
          {amb && (
            <button
              type="button"
              data-testid="ambassador-detail-share"
              onClick={async () => {
                const shareUrl = `${window.location.origin}/api/share/ambassadors/${slug}`;
                try {
                  if (navigator.share) { await navigator.share({ title: amb.display_name, url: shareUrl }); return; }
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success("Share link copied — it shows a preview with their photo and name.");
                } catch { /* user cancelled share */ }
              }}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[2px] text-muted-foreground hover:text-olive-bright transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          )}
        </div>

        {amb === undefined ? (
          <div className="py-24 text-center font-mono text-xs uppercase tracking-[2px] text-muted-foreground" data-testid="ambassador-detail-loading">Loading…</div>
        ) : amb === null ? (
          <div className="py-24 text-center" data-testid="ambassador-detail-notfound">
            <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Ambassador not found</h1>
            <p className="mt-3 text-sm text-secondary-foreground font-body">This ambassador may have been removed or isn't published.</p>
          </div>
        ) : (
          <DetailBody amb={amb} photos={photos} openAt={setLightbox} />
        )}
      </div>

      {lightbox !== null && photos[lightbox] && (
        <div className="fixed inset-0 z-[95] bg-black/95 flex items-center justify-center" onClick={close} data-testid="ambassador-lightbox">
          <button onClick={close} data-testid="ambassador-lightbox-close"
            className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors"><X className="w-8 h-8" /></button>
          {photos.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="ambassador-lightbox-prev"
              className="absolute left-2 sm:left-6 text-white/70 hover:text-white transition-colors"><ChevronLeft className="w-9 h-9" /></button>
          )}
          <img src={photos[lightbox]} onClick={(e) => e.stopPropagation()} alt={`${amb.display_name} — photo ${lightbox + 1}`}
            className="max-w-[92vw] max-h-[86vh] object-contain" data-testid="ambassador-lightbox-img" />
          {photos.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="ambassador-lightbox-next"
              className="absolute right-2 sm:right-6 text-white/70 hover:text-white transition-colors"><ChevronRight className="w-9 h-9" /></button>
          )}
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-xs text-white/70" data-testid="ambassador-lightbox-count">{lightbox + 1} / {photos.length}</span>
        </div>
      )}
    </div>
  );
}

function DetailBody({ amb, photos, openAt }) {
  const isTeam = amb.entry_type === "team";
  const socialLinks = buildSocialLinks(amb.socials);
  const { body: bioBody, signature: bioSig } = splitBio(amb.bio);
  const hero = photos[0];
  const stats = (amb.stats || []).filter((s) => s && (s.label || s.value)).slice(0, 4);
  const firstName = ((amb.display_name || "").trim().split(/\s+/)[0]) || amb.display_name;
  const bioParas = (bioBody || "").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  return (
    <div className="mt-6" data-testid="ambassador-detail">
      {/* HERO — full-bleed photo with name / location / specialty tags overlaid in a gradient scrim */}
      <button type="button" onClick={() => hero && openAt(0)} disabled={!hero}
        className="relative block w-full overflow-hidden border border-sniper group text-left"
        style={{ height: "clamp(360px, 56vw, 520px)" }} data-testid="ambassador-detail-hero">
        {hero ? (
          <img src={hero} alt={amb.display_name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-obsidian text-muted-foreground font-mono text-xs">NO PHOTO</div>
        )}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(5,5,6,0) 0%, rgba(5,5,6,.15) 55%, rgba(5,5,6,.96) 100%)" }} />
        <div className="absolute left-0 right-0 bottom-0 p-6 sm:p-8">
          <span className="inline-block font-mono text-[10px] font-bold uppercase tracking-[1.4px] text-black px-2.5 py-1 mb-3" style={{ background: AMBER }} data-testid="ambassador-detail-badge">
            {isTeam ? "Ambassador Team" : "Ambassador"}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95]" style={{ textShadow: "0 2px 18px rgba(0,0,0,.6)" }} data-testid="ambassador-detail-name">{amb.display_name}</h1>
          {isTeam && amb.team_members?.length > 0 && (
            <p className="mt-2.5 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[1.5px] text-secondary-foreground" data-testid="ambassador-detail-team">
              <Users className="w-3.5 h-3.5 text-olive-bright" /> {amb.team_members.join(" · ")}
            </p>
          )}
          {amb.location && (
            <div className="mt-2 font-mono text-[12px] uppercase tracking-[1.6px] text-[#cfd0c9]" data-testid="ambassador-detail-location">{amb.location}</div>
          )}
          {(amb.specialties || []).length > 0 && (
            <div className="mt-3.5 flex flex-wrap gap-2" data-testid="ambassador-detail-tags">
              {amb.specialties.slice(0, 3).map((s) => (
                <span key={s} className="font-mono text-[10.5px] uppercase tracking-[0.6px] px-2.5 py-1 border" style={{ color: LIME, borderColor: "#3a4318", background: "rgba(12,13,14,.8)" }}>{s}</span>
              ))}
            </div>
          )}
        </div>
        {hero && <span className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[1.5px] text-white bg-black/60 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">View full size →</span>}
      </button>

      {/* FIELD MAP — quick-stat bar (optional; hidden when empty) */}
      {stats.length > 0 && (
        <div className="grid border border-sniper border-t-0 bg-[#0c0d0e]" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0,1fr))` }} data-testid="ambassador-detail-stats">
          {stats.map((s, i) => (
            <div key={i} className={`px-4 sm:px-5 py-4 min-w-0 ${i < stats.length - 1 ? "border-r border-sniper" : ""}`} data-testid={`ambassador-detail-stat-${i}`}>
              {s.label && <div className="font-mono text-[9.5px] uppercase tracking-[0.6px] text-muted-foreground mb-1.5 truncate">{s.label}</div>}
              <div className="font-heading font-bold text-lg sm:text-xl text-foreground leading-tight break-words">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* STORY — full bio as body copy */}
      {bioParas.length > 0 && (
        <div className="mt-9" data-testid="ambassador-detail-story">
          <h2 className="font-heading font-extrabold text-[15px] uppercase tracking-[1px] text-[#8a8a8f] pb-2 mb-4 border-b border-sniper">Story</h2>
          {bioParas.map((p, i) => (
            <p key={i} className="text-[14.5px] leading-[1.75] text-secondary-foreground font-body mb-3.5 whitespace-pre-line">{p}</p>
          ))}
          {bioSig && <p className="mt-1 font-mono text-[11px] uppercase tracking-[2.5px]" style={{ color: LIME }} data-testid="ambassador-detail-signature">{bioSig}</p>}
        </div>
      )}

      {/* FIELD GALLERY — real grid of their uploaded photos */}
      {photos.length > 0 && (
        <div className="mt-9" data-testid="ambassador-detail-gallery-section">
          <h2 className="font-heading font-extrabold text-[15px] uppercase tracking-[1px] text-[#8a8a8f] pb-2 mb-4 border-b border-sniper">Field Gallery — {photos.length} {photos.length === 1 ? "photo" : "photos"}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="ambassador-gallery">
            {photos.map((p, i) => (
              <button key={i} type="button" onClick={() => openAt(i)}
                className="relative overflow-hidden border border-sniper bg-black group focus:outline-none focus-visible:border-olive-bright"
                style={{ aspectRatio: "1 / 1" }} data-testid={`ambassador-gallery-photo-${i}`}>
                <img src={p} alt={`${amb.display_name} — ${i + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.05]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* SHOP CTA — only when a linked (verified) seller account exists */}
      {amb.linked_username && (
        <Link to={`/u/${amb.linked_username}`} className="mt-9 flex items-center justify-between gap-5 flex-wrap p-6 border group"
          style={{ background: "linear-gradient(135deg,#131a08,#0c0d0e)", borderColor: "#3a4318" }} data-testid="ambassador-detail-shop-cta">
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-xl text-foreground">{firstName}'s OpticSwap Listings</div>
            <div className="font-body text-[12.5px] text-secondary-foreground mt-1">Gear they've actually run and sold through — verified seller, live inventory.</div>
          </div>
          <span className="font-mono text-[11.5px] font-bold tracking-[0.5px] text-black px-4 py-3 whitespace-nowrap group-hover:opacity-90 transition-opacity" style={{ background: LIME }}>View Listings →</span>
        </Link>
      )}

      {/* FOOTER — social chips + ID verified badge */}
      {(socialLinks.length > 0 || (amb.linked_verified && amb.linked_username)) && (
        <div className="mt-8 flex items-center justify-between flex-wrap gap-3 pt-5 border-t border-sniper" data-testid="ambassador-detail-footer">
          {socialLinks.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="ambassador-detail-socials">
              {socialLinks.map(({ k, v, key }) => {
                const href = k === "instagram"
                  ? (/^https?:\/\//i.test(v) ? v : `https://instagram.com/${v.replace(/^@/, "")}`)
                  : (v.startsWith("http") ? v : `https://${v}`);
                return (
                  <a key={key} href={href} target="_blank" rel="noopener noreferrer" title={SOCIAL_LABELS[k] || k}
                    className="inline-flex items-center gap-2 border border-[#2a2a2d] bg-black/20 px-3 py-2 hover:border-olive-bright/60 transition-colors"
                    data-testid={`ambassador-detail-social-${key}`}>
                    <span style={{ color: LIME }} className="inline-flex items-center"><SocialIcon k={k} /></span>
                    <span className="font-mono text-[11px] tracking-[0.5px] text-foreground lowercase">{socialDisplay(k, v)}</span>
                  </a>
                );
              })}
            </div>
          ) : <span />}
          {amb.linked_verified && amb.linked_username && (
            <div className="font-mono text-[11px] inline-flex items-center gap-1.5" data-testid="ambassador-detail-verified" style={{ color: "#8fae3f" }}>
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="font-semibold">ID VERIFIED</span>
              <Link to={`/u/${amb.linked_username}`} className="hover:text-foreground text-[#cfd0c9]">· @{amb.linked_username}</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
